import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { cad, FREE_SHIP, FLAT_SHIP } from "@/lib/site";

const CartLine = ({ item, setQty, removeItem }) => (
  <li className="flex gap-4 border-b border-ink/10 py-5" data-testid={`cart-item-${item.key}`}>
    <div className="tile h-24 w-24 shrink-0"><img src={item.image} alt={item.name} className="h-full w-full object-cover mix-blend-multiply" loading="lazy" /></div>
    <div className="flex flex-1 flex-col">
      <div className="flex justify-between gap-2">
        <p className="text-[15px]">{item.name}</p>
        <span className="text-sm">{cad(item.price * item.qty)}</span>
      </div>
      <p className="label mt-1 !text-[10px] text-ink/50">{item.description || `${item.color} · ${item.size === "OS" ? "One size" : item.size}`}</p>
      <div className="mt-auto flex items-center justify-between pt-3">
        <div className="flex items-center border border-ink/30">
          <button onClick={() => setQty(item.key, item.qty - 1)} className="p-2 transition-colors hover:bg-ink/5" aria-label="Decrease quantity" data-testid={`cart-item-decrease-${item.key}`}><Minus size={12} /></button>
          <span className="w-7 text-center font-mono text-xs" data-testid={`cart-item-qty-${item.key}`}>{item.qty}</span>
          <button onClick={() => setQty(item.key, item.qty + 1)} className="p-2 transition-colors hover:bg-ink/5" aria-label="Increase quantity" data-testid={`cart-item-increase-${item.key}`}><Plus size={12} /></button>
        </div>
        <button onClick={() => removeItem(item.key)} aria-label={`Remove ${item.name}`} className="label !text-[10px] text-ink/50 underline underline-offset-4 hover:text-ink" data-testid={`cart-item-remove-${item.key}`}>Remove</button>
      </div>
    </div>
  </li>
);

const ShipBar = ({ subtotal }) => {
  const left = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);
  return (
    <div className="border-b border-ink/10 px-6 py-5" data-testid="cart-shipping-progress">
      <p className="label !text-[10px]" data-testid="cart-shipping-message">
        {left > 0 ? <>{cad(left)} away from free shipping</> : <>Free shipping unlocked</>}
      </p>
      <div className="mt-3 h-[3px] bg-ink/10"><div className="h-full bg-ink transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

export const CartDrawer = () => {
  const { items, count, subtotal, open, setOpen, setQty, removeItem } = useCart();
  const navigate = useNavigate();
  const shipping = items.length === 0 || subtotal >= FREE_SHIP ? 0 : FLAT_SHIP;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  return (
    <div className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div className={`overlay-fade absolute inset-0 bg-ink/30 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} data-testid="cart-overlay" />
      <aside role="dialog" aria-modal="true" aria-label="Shopping cart" className={`drawer-panel absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col bg-cloud ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="cart-drawer">
        <div className="flex h-[88px] items-center justify-between border-b border-ink/10 px-6 lg:h-32">
          <h2 className="label !text-xs font-bold">Cart ({count})</h2>
          <button onClick={() => setOpen(false)} aria-label="Close cart" className="p-1 transition-opacity hover:opacity-60" data-testid="cart-drawer-close"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <ShipBar subtotal={subtotal} />
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 p-8 text-center" data-testid="cart-empty">
            <MascotDoodle variant="headphones" size={120} />
            <p className="text-lg">Your cart is empty.</p>
            <Link to="/shop" onClick={() => setOpen(false)} className="btn btn-ink" data-testid="cart-empty-shop-link">Shop the drop <ArrowRight size={16} /></Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto px-6">{items.map((i) => <CartLine key={i.key} item={i} setQty={setQty} removeItem={removeItem} />)}</ul>
            <div className="space-y-2.5 border-t border-ink/10 px-6 py-6 text-sm">
              <div className="flex justify-between"><span className="text-ink/60">Subtotal</span><span data-testid="cart-subtotal">{cad(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-ink/60">Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? "FREE" : cad(shipping)}</span></div>
              <div className="flex justify-between border-t border-ink/10 pt-3 font-medium"><span>Total (CAD)</span><span data-testid="cart-total">{cad(subtotal + shipping)}</span></div>
              <button onClick={() => { setOpen(false); navigate("/checkout"); }} className="btn btn-ink !mt-5 w-full" data-testid="cart-checkout-button">Checkout <ArrowRight size={16} /></button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};
